import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Progress, Text } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Trash } from 'phosphor-react';
import { toast } from 'sonner';

import { useApiController } from '../hooks/useApiController';

// Exclusão de rota com janela de DESFAZER (decisão do produto): ao confirmar, a rota
// entra numa fila de "exclusão pendente" (aviso persistente no canto sup. direito) e
// só é apagada de fato no backend após ATRASO_MS. O usuário pode navegar livremente e
// desfazer nesse tempo. Timer client-side (sem backend de agendamento) — se fechar o
// navegador antes do prazo, a exclusão não chega a ocorrer (a rota volta), que é o
// trade-off aceito da "opção 1".

const ATRASO_MS = 2 * 60 * 1000; // 2 minutos

interface Pendente {
  recursoId: number;
  nome: string;
  criadoEm: number; // epoch ms (p/ a barra de progresso)
}

interface ExclusaoPendenteCtx {
  // eslint-disable-next-line no-unused-vars
  agendarExclusaoRota: (recursoId: number, nome: string) => void;
  isPendente: (recursoId: number) => boolean;
}

const Ctx = createContext<ExclusaoPendenteCtx | null>(null);

export const useExclusaoPendente = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useExclusaoPendente fora do ExclusaoPendenteProvider');
  return ctx;
};

export function ExclusaoPendenteProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { deleteRecurso } = useApiController();
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  // timers por recursoId (mantidos fora do state p/ não recriar no render)
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const remover = useCallback((recursoId: number) => {
    const t = timers.current[recursoId];
    if (t) {
      clearTimeout(t);
      delete timers.current[recursoId];
    }
    setPendentes(prev => prev.filter(p => p.recursoId !== recursoId));
  }, []);

  const confirmarExclusao = useCallback(
    async (recursoId: number, nome: string) => {
      delete timers.current[recursoId];
      setPendentes(prev => prev.filter(p => p.recursoId !== recursoId));
      try {
        await deleteRecurso(recursoId);
        queryClient.invalidateQueries({ queryKey: ['api-controller-recursos'] });
        queryClient.invalidateQueries({ queryKey: ['api-controller-tokens'] });
        toast.success(`Rota "${nome}" excluída`, { position: 'top-right' });
      } catch {
        toast.error(`Falha ao excluir a rota "${nome}"`, { position: 'top-right' });
      }
    },
    [deleteRecurso, queryClient],
  );

  const agendarExclusaoRota = useCallback(
    (recursoId: number, nome: string) => {
      // se já estava pendente, reinicia o timer
      if (timers.current[recursoId]) clearTimeout(timers.current[recursoId]);
      setPendentes(prev => [
        ...prev.filter(p => p.recursoId !== recursoId),
        { recursoId, nome, criadoEm: Date.now() },
      ]);
      timers.current[recursoId] = setTimeout(() => confirmarExclusao(recursoId, nome), ATRASO_MS);
    },
    [confirmarExclusao],
  );

  const desfazer = useCallback(
    (recursoId: number, nome: string) => {
      remover(recursoId);
      toast.info(`Exclusão da rota "${nome}" cancelada`, { position: 'top-right' });
    },
    [remover],
  );

  const isPendente = useCallback(
    (recursoId: number) => pendentes.some(p => p.recursoId === recursoId),
    [pendentes],
  );

  const value = useMemo(
    () => ({ agendarExclusaoRota, isPendente }),
    [agendarExclusaoRota, isPendente],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* Aviso persistente (canto superior direito) — sobrevive à navegação. */}
      {pendentes.length > 0 && (
        <Box position="fixed" top="72px" right="16px" zIndex={2000} width="320px">
          {pendentes.map(p => (
            <AvisoExclusao
              key={p.recursoId}
              pendente={p}
              onDesfazer={() => desfazer(p.recursoId, p.nome)}
            />
          ))}
        </Box>
      )}
    </Ctx.Provider>
  );
}

function AvisoExclusao({ pendente, onDesfazer }: { pendente: Pendente; onDesfazer: () => void }) {
  // barra de progresso do tempo restante (atualiza a cada 1s)
  const [agora, setAgora] = useState(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const decorrido = agora - pendente.criadoEm;
  const restanteMs = Math.max(0, ATRASO_MS - decorrido);
  const restanteS = Math.ceil(restanteMs / 1000);
  const pct = Math.min(100, (decorrido / ATRASO_MS) * 100);

  return (
    <Box bg="red.600" color="white" p={3} borderRadius="md" boxShadow="lg" mb={2}>
      <Flex align="center" gap={2} mb={1}>
        <Trash size={18} weight="bold" />
        <Text fontSize="sm" fontWeight="600" noOfLines={1} flex={1}>
          Excluindo rota &quot;{pendente.nome}&quot;
        </Text>
        <Button size="xs" variant="outline" colorScheme="whiteAlpha" onClick={onDesfazer}>
          Desfazer
        </Button>
      </Flex>
      <Text fontSize="xs" opacity={0.85} mb={1}>
        Será excluída em {Math.floor(restanteS / 60)}:{String(restanteS % 60).padStart(2, '0')} — os
        tokens dela serão revogados.
      </Text>
      <Progress value={pct} size="xs" colorScheme="whiteAlpha" borderRadius="full" />
    </Box>
  );
}
